import React, { useState, useCallback, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ROLE_LABELS, generateTierName, stepId } from "../policyBuilderState";
import AddStepModal from "./AddStepModal";

const SortableStepCard = ({ step, onRemove, onEdit, onToggleOverride, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const modeLabel = step.approval_mode === "all_of" ? "All of" : "Any of";
  const typeLabel = step.approver_type === "user" ? "User" : "Role";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-card-bd bg-card rounded-ctrl p-3 flex items-center gap-3 hover:bg-surface-2"
    >
      {!disabled && (
        <span {...attributes} {...listeners} className="cursor-grab text-text-3 hover:text-text-2">
          <FontAwesomeIcon icon={icon({ name: "grip-vertical", style: "solid" })} className="text-xs" />
        </span>
      )}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onEdit(step)}>
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-text">{step.label || step.role || "Step"}</span>
          <span className="text-xs px-1.5 py-0.5 bg-surface-2 rounded text-text-2">{modeLabel}</span>
          <span className="text-xs px-1.5 py-0.5 bg-surface-2 rounded text-text-2">{typeLabel}</span>
        </div>
        {step.allow_duplicate_override && (
          <div className="text-xs text-text-2 mt-1">Duplicate override enabled</div>
        )}
      </div>
      {!disabled && (
        <div className="flex gap-1 shrink-0 items-center">
          <button
            type="button"
            onClick={() => onEdit(step)}
            className="p-1 text-text-3 hover:text-text-2 rounded text-xs"
            title="Edit step"
            aria-label={`Edit step ${step.label || step.role || "Step"}`}
          >
            Edit
          </button>
          <button
            onClick={() => onToggleOverride(step.id)}
            className={`p-1 rounded text-xs ${step.allow_duplicate_override ? "text-success" : "text-text-3 hover:text-text-2"}`}
            title="Toggle duplicate override"
          >
            <FontAwesomeIcon icon={icon({ name: "clone", style: "regular" })} />
          </button>
          <button
            onClick={() => onRemove(step.id)}
            className="p-1 text-danger hover:text-danger rounded"
            title="Remove step"
          >
            <FontAwesomeIcon icon={icon({ name: "trash", style: "regular" })} className="text-xs" />
          </button>
        </div>
      )}
    </div>
  );
};

const ApprovalChainPanel = ({ tier, onTierChange, disabled, errors, warnings, embedded, addStepRef }) => {
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [stepModalMode, setStepModalMode] = useState("add");
  const [editingStep, setEditingStep] = useState(null);
  const [editingStepIndex, setEditingStepIndex] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const stepIds = tier.chain.map((s) => s.id);
  const tierName = tier.name || generateTierName(tier);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = stepIds.indexOf(active.id);
      const newIndex = stepIds.indexOf(over.id);
      onTierChange({ ...tier, chain: arrayMove(tier.chain, oldIndex, newIndex) });
    }
  };

  const handleRemoveStep = (stepIdToRemove) => {
    const wasEditingRemoved =
      stepModalOpen && stepModalMode === "edit" && editingStep?.id === stepIdToRemove;
    onTierChange({ ...tier, chain: tier.chain.filter((s) => s.id !== stepIdToRemove) });
    if (wasEditingRemoved) {
      setStepModalOpen(false);
      setEditingStep(null);
      setEditingStepIndex(null);
    }
  };

  const handleToggleOverride = (stepId) => {
    onTierChange({
      ...tier,
      chain: tier.chain.map((s) =>
        s.id === stepId ? { ...s, allow_duplicate_override: !s.allow_duplicate_override } : s
      ),
    });
  };

  const handleStepModalSubmit = (stepDraft, existingStep) => {
    if (stepModalMode === "add") {
      const newStep = {
        id: stepId(),
        ...stepDraft,
        allow_duplicate_override: false,
      };
      onTierChange({ ...tier, chain: [...tier.chain, newStep] });
    } else {
      const updatedStep = { ...editingStep, ...stepDraft };
      const nextChain = editingStep?.id
        ? tier.chain.map((s) => (s.id === editingStep.id ? updatedStep : s))
        : tier.chain.map((s, i) => (i === editingStepIndex ? updatedStep : s));
      onTierChange({ ...tier, chain: nextChain });
    }
    setStepModalOpen(false);
    setEditingStep(null);
    setEditingStepIndex(null);
  };

  // useCallback: this is handed to the parent through useImperativeHandle below,
  // and it only calls state setters, which React guarantees are stable.
  const handleOpenAddModal = useCallback(() => {
    setStepModalMode("add");
    setEditingStep(null);
    setEditingStepIndex(null);
    setStepModalOpen(true);
  }, []);

  useImperativeHandle(addStepRef, () => ({ openAddModal: handleOpenAddModal }), [handleOpenAddModal]);

  const handleOpenEditModal = (step, index) => {
    setStepModalMode("edit");
    setEditingStep(step);
    setEditingStepIndex(index);
    setStepModalOpen(true);
  };

  const chainLabels = tier.chain.map((s) => s.label || (s.role ? ROLE_LABELS[s.role] : "User"));
  const chainSentence = chainLabels.length > 0
    ? `Approvals required in order: ${chainLabels.join(" → ")}`
    : "No approval steps defined.";

  const content = (
    <div className="space-y-4">
      {!embedded && (
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text">
            Chain — {tierName}
          </h3>
        </div>
      )}

      {/* Errors / Warnings */}
      {errors.length > 0 && (
        <div className="px-3 py-2 bg-danger-soft border border-danger rounded text-xs text-danger space-y-1">
          {errors.map((e, i) => <div key={i}>{e.message}</div>)}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="px-3 py-2 bg-warning-soft border border-warning rounded text-xs text-warning space-y-1">
          {warnings.map((w, i) => <div key={i}>{w.message}</div>)}
        </div>
      )}

      {/* Steps */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={stepIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {tier.chain.map((step, index) => (
              <SortableStepCard
                key={step.id}
                step={step}
                onRemove={handleRemoveStep}
                onEdit={(s) => handleOpenEditModal(s, index)}
                onToggleOverride={handleToggleOverride}
                disabled={disabled}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {tier.chain.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 px-4 border border-dashed border-border rounded-ctrl bg-surface-2 text-center">
          <p className="text-sm font-medium text-text-2 mb-1">No approval steps yet</p>
          <p className="text-sm text-text-2">Add at least one approver step to activate this tier.</p>
        </div>
      )}

      {/* Chain sentence summary */}
      <div className="pt-3 border-t border-border text-xs text-text-2 italic">
        {chainSentence}
      </div>

      <AddStepModal
        show={stepModalOpen}
        onClose={() => {
          setStepModalOpen(false);
          setEditingStep(null);
          setEditingStepIndex(null);
        }}
        onSubmit={handleStepModalSubmit}
        mode={stepModalMode}
        initialStep={stepModalMode === "edit" ? editingStep : null}
      />
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 space-y-4">
      {content}
    </div>
  );
};

ApprovalChainPanel.propTypes = {
  tier: PropTypes.object.isRequired,
  onTierChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.array,
  warnings: PropTypes.array,
  embedded: PropTypes.bool,
  addStepRef: PropTypes.object,
};

export default ApprovalChainPanel;
